// ATLAS-5883: pure helper that writes the rules of a template's <style> blocks onto the elements they style, so
// recipients still see them when their mail client removes the block. Runs on the email export, after the <style>
// blocks were restored and the root defaults stripped. The <style> blocks themselves are kept as they are.
//
// Only simple selectors are inlined (type, .class, #id, compounds of those, joined by descendant whitespace). Anything
// else (pseudo-classes and -elements, > + ~ combinators, *, [attr], escapes, nesting) is left to the <style> block, and
// blocks inlining of the properties it sets on the elements it matches, so the editor's rendering does not change.
//
// SYNC: This file exists in two byte-identical copies:
//   Frontend/Atlas.Spa.Backoffice/src/app/shared/widgets/ckeditor-content-style-inline.ts
//   Frontend/Atlas.Spa.Portal/src/app/shared/widgets/ckeditor-content-style-inline.ts
// Keep them in sync; any change goes into both (operator decision 2026-10-13: no shared front-end module).
//
// Only runtime import: filterStyleRules (also in the Portal copy of ckeditor-content-style.ts).
import { filterStyleRules } from './ckeditor-content-style';

interface Declaration {
  name: string;
  value: string;
  important: boolean;
}

interface InlinableRule {
  member: string;
  matchSelector: string;
  specificity: [number, number, number]; // ids, classes, types
  order: number;
  declarations: Declaration[];
}

interface NonInlinableRule {
  member: string;
  declarations: Declaration[];
}

interface Winner {
  value: string;
  important: boolean;
  specificity: [number, number, number];
  order: number;
}

type Winners = Map<Element, Map<string, Winner>>;
type Blocked = Map<Element, Set<string>>;

/** Records what the inliner wrote on an element, as `longhand:value;...` in CSSOM form (ATLAS-5883 R2). */
export const INLINED_RECORD_ATTRIBUTE = 'data-wl-inlined';
const CARRY_OVER_PROPERTIES = ['color', 'background-color', 'font-size', 'font-family'];

const IDENT = '-?[_a-zA-Z][_a-zA-Z0-9-]*';
const COMPOUND = `(?:${IDENT})?(?:[.#]${IDENT})*`;
const SUPPORTED_MEMBER = new RegExp(`^${COMPOUND}(?:[ \\t\\n\\f\\r]+${COMPOUND})*$`);
const BARE_ROOT = /^(html|body)$/i;
const ROOT_COMPOUND_AT_START = /^(html|body|:root)(?=[ \t\n\f\r>+~]|$)/i;

function parseInert(html: string): HTMLTemplateElement {
  const template = document.createElement('template');
  template.innerHTML = html;
  return template;
}

/** Splits a selector list at top-level commas, respecting (), [], quoted strings and backslash escapes. */
function splitSelectorList(text: string): string[] {
  const parts: string[] = [];
  let current = '';
  let depth = 0;
  let quote = '';
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '\\') {
      current += ch + (text[i + 1] ?? '');
      i++;
      continue;
    }
    if (quote) {
      if (ch === quote) quote = '';
    } else if (ch === '"' || ch === "'") {
      quote = ch;
    } else if (ch === '(' || ch === '[') {
      depth++;
    } else if ((ch === ')' || ch === ']') && depth > 0) {
      depth--;
    } else if (depth === 0 && ch === ',') {
      parts.push(current);
      current = '';
      continue;
    }
    current += ch;
  }
  parts.push(current);
  return parts;
}

function ruleDeclarations(rule: CSSStyleRule): Declaration[] {
  const declarations: Declaration[] = [];
  for (let i = 0; i < rule.style.length; i++) {
    const name = rule.style[i];
    const value = rule.style.getPropertyValue(name);
    if (!value) continue; // a shorthand that holds var() has no longhand value
    declarations.push({ name, value, important: rule.style.getPropertyPriority(name) === 'important' });
  }
  return declarations;
}

function specificityOf(member: string): [number, number, number] {
  const compounds = member.split(/[ \t\n\f\r]+/).filter(Boolean);
  let ids = 0;
  let classes = 0;
  let types = 0;
  for (const compound of compounds) {
    ids += (compound.match(/#/g) ?? []).length;
    classes += (compound.match(/\./g) ?? []).length;
    if (/^[_a-zA-Z-]/.test(compound)) types++;
  }
  return [ids, classes, types];
}

/** The inlinable members and, separately, the unsupported members of every top-level style rule (R1.9). */
function collectInlinableRules(fragment: DocumentFragment): {
  inlinable: InlinableRule[];
  nonInlinable: NonInlinableRule[];
} {
  const inlinable: InlinableRule[] = [];
  const nonInlinable: NonInlinableRule[] = [];
  let order = 0;
  fragment.querySelectorAll('style').forEach((styleEl) => {
    for (const rule of filterStyleRules(styleEl.textContent ?? '')) {
      order++;
      const declarations = ruleDeclarations(rule);
      if (!declarations.length) continue;
      for (const raw of splitSelectorList(rule.selectorText)) {
        const member = raw.trim();
        if (!member) continue;
        if (!SUPPORTED_MEMBER.test(member)) {
          nonInlinable.push({ member, declarations });
          continue;
        }
        // leading bare html/body compounds mirror the editor's own display scoping and match nothing in a fragment
        const compounds = member.split(/[ \t\n\f\r]+/);
        while (compounds.length && BARE_ROOT.test(compounds[0])) compounds.shift();
        if (!compounds.length) continue;
        inlinable.push({
          member,
          matchSelector: compounds.join(' '),
          specificity: specificityOf(member),
          order,
          declarations,
        });
      }
    }
  });
  return { inlinable, nonInlinable };
}

/** Whether `el` matches an unsupported member, mapping a leading html/body/:root compound as scopeMember does. */
function matchesNonInlinable(fragment: DocumentFragment, el: Element, member: string): boolean {
  let rest = member.trim();
  let rooted = false;
  let childOfRoot = false;
  for (;;) {
    const root = ROOT_COMPOUND_AT_START.exec(rest);
    if (!root) break;
    rooted = true;
    rest = rest.slice(root[0].length);
    const combinator = /^[ \t\n\f\r]*([>+~])?[ \t\n\f\r]*/.exec(rest)!;
    rest = rest.slice(combinator[0].length);
    if (combinator[1] === '+' || combinator[1] === '~') return false;
    childOfRoot = combinator[1] === '>';
    if (!rest) return false; // matches the root itself, which is not part of the fragment
  }
  if (!el.matches(rest)) return false;
  if (rooted && childOfRoot && !/[ \t\n\f\r>+~]/.test(rest.replace(/\([^)]*\)|\[[^\]]*\]/g, ''))) {
    return el.parentNode === fragment;
  }
  return true;
}

/** Properties that a non-inlinable rule sets on each element it matches; inlining them would change the rendering. */
function collectBlockedProperties(fragment: DocumentFragment, nonInlinable: NonInlinableRule[]): Blocked {
  const blocked: Blocked = new Map();
  if (!nonInlinable.length) return blocked;
  fragment.querySelectorAll('*').forEach((el) => {
    if (el.localName === 'style') return;
    for (const { member, declarations } of nonInlinable) {
      let matched = false;
      try {
        matched = matchesNonInlinable(fragment, el, member);
      } catch {
        continue; // not valid for matches
      }
      if (!matched) continue;
      let set = blocked.get(el);
      if (!set) blocked.set(el, (set = new Set()));
      declarations.forEach((d) => set!.add(d.name));
    }
  });
  return blocked;
}

function beats(a: Winner, b: Winner): boolean {
  if (a.important !== b.important) return a.important;
  for (let i = 0; i < 3; i++) {
    if (a.specificity[i] !== b.specificity[i]) return a.specificity[i] > b.specificity[i];
  }
  return a.order >= b.order;
}

/** One winning declaration per (element, property): !important, then specificity, then later order. */
function computeWinners(fragment: DocumentFragment, rules: InlinableRule[], blocked: Blocked): Winners {
  const winners: Winners = new Map();
  for (const rule of rules) {
    let matches: NodeListOf<Element>;
    try {
      matches = fragment.querySelectorAll(rule.matchSelector);
    } catch {
      continue;
    }
    matches.forEach((el) => {
      if (el.localName === 'style') return;
      const blockedProps = blocked.get(el);
      let props = winners.get(el);
      for (const d of rule.declarations) {
        if (blockedProps?.has(d.name)) continue;
        if (!props) winners.set(el, (props = new Map()));
        const candidate: Winner = {
          value: d.value,
          important: d.important,
          specificity: rule.specificity,
          order: rule.order,
        };
        const current = props.get(d.name);
        if (!current || beats(candidate, current)) props.set(d.name, candidate);
      }
    });
  }
  return winners;
}

/** Writes the winning declarations onto `el`; returns the ones it actually wrote. */
function applyWinners(el: HTMLElement, winners: Map<string, Winner>): Declaration[] {
  // re-serialising through the CSSOM drops declarations the browser does not recognise (Outlook's mso-* ones)
  const mso = (el.getAttribute('style') ?? '')
    .split(';')
    .map((d) => d.trim())
    .filter((d) => /^mso-/i.test(d));
  const written: Declaration[] = [];
  winners.forEach((winner, name) => {
    if (el.style.getPropertyValue(name) && !(winner.important && el.style.getPropertyPriority(name) !== 'important')) {
      return; // the element's own declaration wins
    }
    el.style.setProperty(name, winner.value);
    written.push({ name, value: winner.value, important: winner.important });
  });
  if (written.length && mso.length) {
    el.setAttribute('style', `${el.style.cssText} ${mso.join(';')};`.trim());
  }
  return written;
}

interface PrePassResult {
  recorded: Set<HTMLElement>;
  emptied: Set<HTMLElement>;
  changed: boolean;
}

/** Whether the recorded longhand is still on `style` with the recorded value and without a priority. Both sides went
 *  through the CSSOM, so hex/case/quote/shorthand differences normalise the same way; never compare attribute text. */
function recordMatches(style: CSSStyleDeclaration, rec: CSSStyleDeclaration, name: string): boolean {
  const value = style.getPropertyValue(name);
  return value !== '' && value === rec.getPropertyValue(name) && style.getPropertyPriority(name) === '';
}

/** Font plugin spans (priority 7) end up inside the GHS span (priority 6) that carries the record after a reload. */
function carryOverToInnerSpan(el: HTMLElement, rec: CSSStyleDeclaration, name: string): boolean {
  if (el.localName !== 'span' || el.childNodes.length !== 1) return false;
  const child = el.firstChild as HTMLElement;
  if (child.nodeType !== Node.ELEMENT_NODE || child.localName !== 'span') return false;
  if (!child.hasAttribute('style') || Array.from(child.attributes).some((a) => a.name !== 'style' && a.name !== 'id')) {
    return false; // a Font plugin span carries a style only; an id is tolerated so an anchor-less wrapper still unwraps
  }
  if (/mso-/i.test(child.getAttribute('style') ?? '')) return false;
  if (!recordMatches(child.style, rec, name)) return false;
  child.style.removeProperty(name);
  if (child.style.length === 0) child.replaceWith(...Array.from(child.childNodes));
  return true;
}

/**
 * Removes the values recorded by an earlier export (R2.1) so the cascade is derived again from the current blocks.
 * A recorded value the author changed no longer matches and is kept as theirs (R2.3). Known limitation: an author who
 * deliberately sets exactly the recorded value is indistinguishable from the inlined one and loses it when the block
 * changes. Also, a block !important that replaced an author's normal inline value (R1.6) is recorded like any other
 * write, so the author's original value does not come back once the rule is removed.
 */
function stripRecordedDeclarations(fragment: DocumentFragment): PrePassResult {
  const result: PrePassResult = { recorded: new Set(), emptied: new Set(), changed: false };
  fragment.querySelectorAll(`[${INLINED_RECORD_ATTRIBUTE}]`).forEach((node) => {
    const el = node as HTMLElement;
    if (el.localName === 'style') return;
    result.recorded.add(el);
    const rec = document.createElement('div').style;
    rec.cssText = el.getAttribute(INLINED_RECORD_ATTRIBUTE) ?? '';
    const names: string[] = [];
    for (let i = 0; i < rec.length; i++) names.push(rec[i]);
    const mso = (el.getAttribute('style') ?? '')
      .split(';')
      .map((d) => d.trim())
      .filter((d) => /^mso-/i.test(d));
    let removed = false;
    for (const name of names) {
      if (recordMatches(el.style, rec, name)) {
        el.style.removeProperty(name);
        removed = true;
      } else if (
        el.style.getPropertyValue(name) === '' &&
        CARRY_OVER_PROPERTIES.includes(name) &&
        carryOverToInnerSpan(el, rec, name)
      ) {
        removed = true;
      }
    }
    if (!removed) return;
    result.changed = true;
    if (mso.length) el.setAttribute('style', `${el.style.cssText} ${mso.join(';')};`.trim());
    else if (el.style.length === 0) result.emptied.add(el);
  });
  return result;
}

/** The record for what applyWinners wrote: longhand:value in write order, values read back from the CSSOM. */
function recordOf(el: HTMLElement, written: Declaration[]): string {
  return written.map((d) => `${d.name}:${el.style.getPropertyValue(d.name)}`).join(';');
}

/** Inlines the supported rules of every <style> block in `html` onto the elements they match, and records what it wrote
 *  in `data-wl-inlined` (R2). On every call the values recorded earlier are stripped first and derived again from the
 *  current blocks, so an edited or deleted block takes effect; author-changed values are kept. Pure and idempotent;
 *  returns `html` unchanged when it has no <style> and no record, when nothing changes, or if anything throws. The
 *  <style> blocks are kept as-is. */
export function inlineStyleBlocks(html: string): string {
  if (!/<style/i.test(html) && !/data-wl-inlined/i.test(html)) return html;
  try {
    const template = parseInert(html);
    const fragment = template.content;
    const pre = stripRecordedDeclarations(fragment);
    const { inlinable, nonInlinable } = collectInlinableRules(fragment);
    const blocked = collectBlockedProperties(fragment, nonInlinable);
    const winners = inlinable.length ? computeWinners(fragment, inlinable, blocked) : new Map();
    let changed = pre.changed;
    const marked = new Set<HTMLElement>();
    winners.forEach((props, el) => {
      const target = el as HTMLElement;
      const written = applyWinners(target, props);
      if (!written.length) return;
      // fixed attribute order (marker, then style) so that every pass serialises alike, wherever the input had them
      const record = recordOf(target, written);
      const css = target.getAttribute('style') ?? '';
      target.removeAttribute('style');
      target.removeAttribute(INLINED_RECORD_ATTRIBUTE);
      target.setAttribute(INLINED_RECORD_ATTRIBUTE, record);
      target.setAttribute('style', css);
      marked.add(target);
      changed = true;
    });
    pre.recorded.forEach((el) => {
      if (marked.has(el)) return;
      el.removeAttribute(INLINED_RECORD_ATTRIBUTE);
      if (pre.emptied.has(el) && el.style.length === 0) el.removeAttribute('style');
      changed = true;
    });
    return changed ? template.innerHTML : html;
  } catch {
    return html;
  }
}
